import React, { useState } from "react";
import { motion } from "motion/react";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Search, Filter } from "lucide-react";
import { Input } from "../Components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../Components/ui/tabs";
import { Skeleton } from "../Components/ui/skeleton";
import ArticleCard from "../Components/articles/ArticleCard";

const categories = [
  { value: "all", label: "All Articles" },
  { value: "sun-safety", label: "Sun Safety" },
  { value: "skin-health", label: "Skin Health" },
  { value: "technology", label: "Technology" },
  { value: "research", label: "Research" },
  { value: "tips", label: "Tips" }
];

export default function Articles() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  React.useEffect(() => {
    const sync = () => setTheme(localStorage.getItem("theme") || "dark");
    window.addEventListener("storage", sync);
    window.addEventListener("themechange", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("themechange", sync);
    };
  }, []);

  const { data: articles, isLoading } = useQuery({
    queryKey: ['articles'],
    queryFn: () => base44.entities.Article.filter({ published: true }, "-created_date"),
    initialData: []
  });

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticle = filteredArticles.find(a => a.featured);
  const regularArticles = filteredArticles.filter(a => !a.featured);

  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-[#05070d] text-slate-100" : "bg-[#fff8f3] text-slate-900"
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 py-20 text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,0.35),transparent_45%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur-md">
              <BookOpen className="h-4 w-4" />
              Knowledge hub
            </div>
            <h1 className="font-display text-5xl font-semibold tracking-tight md:text-6xl">Sun safety articles</h1>
            <p className="mt-4 text-xl text-white/90">
              Expert insights on UV protection, skin health, and responsible tanning
            </p>

            <div className="relative mx-auto mt-8 max-w-2xl">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-14 border-0 bg-white/95 pl-12 text-lg shadow-xl backdrop-blur-sm"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="flex items-center gap-4 mb-8 overflow-x-auto pb-2">
          <Filter className={`w-5 h-5 flex-shrink-0 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className={theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}>
              {categories.map(cat => (
                <TabsTrigger key={cat.value} value={cat.value} className={
                  theme === 'dark' ? 'data-[state=active]:bg-gray-700 data-[state=active]:text-white text-gray-300' : ''
                }>
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Results Count */}
        <div className="mb-8">
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
            {isLoading ? 'Loading...' : `${filteredArticles.length} ${filteredArticles.length === 1 ? 'article' : 'articles'} found`}
          </p>
        </div>

        {/* Articles Grid */}
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(6).fill(0).map((_, i) => (
              <div key={i} className={`border-2 rounded-xl overflow-hidden ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <Skeleton className="h-48 w-full" />
                <div className="p-6 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className={`w-16 h-16 mx-auto mb-4 ${
              theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>No articles found</h3>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {featuredArticle && <ArticleCard article={featuredArticle} featured />}
            {regularArticles.map(article => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
